// src/features/records/hooks/useGroupRecordCounts.js

import { useState, useEffect, useRef, useMemo } from 'react';
import { getGroupRecordCount } from '../services/edges/counts';

/**
 * Hook to fetch and cache record counts for multiple groups
 * 
 * Implementation Notes:
 * - Currently uses getCountFromServer() for each group
 * - Individual group caching allows partial cache hits
 * - Sequence guard prevents race conditions
 * 
 * Future Optimization:
 * With denormalized counters at /groups/{gid}/stats/recordCount,
 * we could batch-read all counts in a single query or use
 * real-time listeners for live count updates in the UI.
 * 
 * @param {Array<string>} groupIds - Array of group IDs to fetch counts for
 * @param {Object} options - Options object
 * @param {boolean} options.enabled - Whether to fetch counts (default: true)
 * @param {number} options.ttl - Cache TTL in milliseconds (default: 5 minutes)
 * @returns {Object} { counts: Map<string, number>, loading: boolean, error: Error|null }
 */
function useGroupRecordCounts(groupIds, options = {}) {
  const { enabled = true, ttl = 5 * 60 * 1000 } = options;
  
  const [counts, setCounts] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);
  
  // Cache with TTL
  const cacheRef = useRef(new Map()); // Map<groupId, { count: number, timestamp: number }>
  const sequenceRef = useRef(0); // Sequence guard instead of abort controller
  
  // Create a stable key from groupIds for dependency tracking
  // Use useMemo to ensure this only changes when groupIds content changes
  const groupIdsKey = useMemo(() => {
    if (!groupIds || groupIds.length === 0) return '';
    return [...groupIds].sort().join(',');
  }, [groupIds]);
  
  useEffect(() => {
    if (!enabled || !groupIds || groupIds.length === 0) {
      setCounts(new Map());
      return;
    }
    
    // Increment sequence for this request
    const currentSequence = ++sequenceRef.current;
    
    const fetchCounts = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const countsMap = new Map();
        
        // Process groups in parallel for better performance
        const countPromises = groupIds.map(async (groupId) => {
          // Check cache first
          const cached = cacheRef.current.get(groupId);
          if (cached && Date.now() - cached.timestamp < ttl) {
            return { groupId, count: cached.count };
          }
          
          // Fetch from service
          const count = await getGroupRecordCount(groupId);
          
          // Update cache
          cacheRef.current.set(groupId, {
            count,
            timestamp: Date.now()
          });
          
          return { groupId, count };
        });
        
        const results = await Promise.all(countPromises);
        
        // Check if this is still the current request
        if (currentSequence !== sequenceRef.current) {
          return; // Stale request, ignore results
        }
        
        // Build the counts map
        results.forEach(({ groupId, count }) => {
          countsMap.set(groupId, count);
        });
        
        setCounts(countsMap);
      } catch (err) {
        // Only handle error if this is still the current request
        if (currentSequence !== sequenceRef.current) {
          return;
        }
        
        console.error('Error fetching group record counts:', err);
        setError(err);
        // Set empty counts on error
        const emptyCounts = new Map();
        groupIds.forEach(id => emptyCounts.set(id, 0));
        setCounts(emptyCounts);
      } finally {
        // Only update loading state if this is still the current request
        if (currentSequence === sequenceRef.current) {
          setLoading(false);
        }
      }
    };
    
    fetchCounts();
  }, [groupIdsKey, groupIds, enabled, ttl, refreshToken]);
  
  // Helper function to get count for a specific group
  const getCountForGroup = (groupId) => {
    return counts.get(groupId) || 0;
  };
  
  // Helper function to refresh counts
  const refresh = () => {
    // Clear cache for all groups
    groupIds?.forEach(id => cacheRef.current.delete(id));
    // Increment refresh token to trigger re-fetch
    setRefreshToken(prev => prev + 1);
  };
  
  return {
    counts,
    getCountForGroup,
    loading,
    error,
    refresh
  };
}

export default useGroupRecordCounts;
